import React from 'react';
import { ui } from '../../shared/ui';

/** m.1.2 Posrednici - zajednicki tipovi i sitni elementi forme. */

export type Props = { token: string | null; isAdmin: boolean; onError: (msg: string) => void };

export type Intermediary = {
    id: number; name: string; oib: string | null; kind: string; vat_registered: number | boolean; beneficial_owner: string | null;
    contact_person: string | null; email: string | null; phone: string | null; iban: string | null;
    contract_date: string | null; termination_date: string | null; notice_end_date: string | null;
    kyc_status: string; sanctions_pep_ok: number | boolean; criminal_record_ok: number | boolean;
    conflict_of_interest_ok: number | boolean; conflict_exception_approved: number | boolean;
    code_of_conduct_signed_at: string | null; training_completed_at: string | null;
    approved_by_name: string | null; aml_confirmed_by_name: string | null; probation_until: string | null;
    status: string; note: string | null; missing: string[]; leads: number; clients: number;
};

export type Lead = {
    id: number; intermediary_id: number; intermediary_name: string; client_name: string; client_oib: string | null;
    client_email: string | null; client_phone: string | null; reported_at: string; consent_given: number | boolean;
    expires_at: string; decision_due_at: string | null; outcome: string; decided_by_name: string | null;
    customer_code: string | null; first_purchase_at: string | null; commission_until: string | null; check_detail: string | null;
};

export type Attribution = {
    id: number; intermediary_name: string; client_name: string; broj_ponude: string; customer_code: string;
    naziv_artikla: string | null; poslovnica: string | null; kolicina: string; iznos_sa_pdv: string; datum_prihvacanja: string;
    ruc_eur: string | null; list_commission_eur: string | null; cap_eur: string | null; commission_eur: string;
    sale_vat_rate: string | null; vat_rate: string; vat_eur: string;
    status: string; settlement_id: number | null; commission_until: string | null;
};

export type Settlement = {
    id: number; intermediary_id: number; intermediary_name: string; period: string; commission_eur: string;
    deductions_eur: string; carried_in_eur: string; total_eur: string; payable_eur: string; carried_out_eur: string;
    vat_eur: string; payable_vat_eur: string; carried_out_vat_eur: string;
    status: string; invoice_number: string | null; paid_at: string | null;
};

export const STATUS_LABEL: Record<string, string> = {
    kandidat: 'Kandidat', probni: 'Probni rok', aktivan: 'Aktivan', suspendiran: 'Suspendiran', raskinut: 'Raskinut',
};
export const OUTCOME_LABEL: Record<string, string> = {
    ceka: 'Čeka odluku', prihvacen: 'Prihvaćen', poznat_klijent: 'Poznat klijent', drugi_posrednik: 'Drugi posrednik',
    kampanja: 'Kampanja', odbijen: 'Odbijen',
};
export const ATTR_LABEL: Record<string, string> = {
    obracunato: 'Obračunato', bez_cjenika: 'Nema u cjeniku', bez_ruca: 'Nema RUC-a', storno: 'Storno',
};
export const SETTLEMENT_LABEL: Record<string, string> = { nacrt: 'Nacrt', zakljucen: 'Zaključen', placen: 'Plaćen' };

export function tone(s: string): 'ok' | 'warn' | 'bad' | 'info' | 'neutral' {
    if (['aktivan', 'prihvacen', 'obracunato', 'placen', 'A'].includes(s)) return 'ok';
    if (['probni', 'ceka', 'bez_cjenika', 'bez_ruca', 'nacrt', 'B'].includes(s)) return 'warn';
    if (['suspendiran', 'raskinut', 'odbijen', 'storno', 'poznat_klijent', 'drugi_posrednik', 'C'].includes(s)) return 'bad';
    if (s === 'zakljucen') return 'info';
    return 'neutral';
}

export const eur = (v: string | number | null | undefined): string =>
    v === null || v === undefined || v === '' ? '-' : new Intl.NumberFormat('hr-HR', { style: 'currency', currency: 'EUR' }).format(Number(v));
export const num = (v: string | number | null | undefined, d = 2): string =>
    v === null || v === undefined || v === '' ? '-' : new Intl.NumberFormat('hr-HR', { maximumFractionDigits: d }).format(Number(v));
export const day = (v: string | null | undefined): string => {
    if (!v) return '-';
    const [y, m, d] = v.slice(0, 10).split('-');
    return `${d}.${m}.${y}.`;
};
export const dayTime = (v: string | null | undefined): string => (v ? `${day(v)} ${v.slice(11, 16)}` : '-');
export const yes = (v: number | boolean | null | undefined): boolean => v === true || v === 1;

const inputStyle: React.CSSProperties = {
    height: 36, padding: '0 10px', borderRadius: ui.radiusSm, border: `1px solid ${ui.line}`, fontSize: '0.85rem', color: ui.ink, background: '#fff', width: '100%', boxSizing: 'border-box',
};

/** Polje forme s natpisom iznad. */
export function Field({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.75rem', color: ui.ink2, fontWeight: 650, gridColumn: wide ? '1 / -1' : undefined }}>
            {label}
            {children}
        </label>
    );
}

export function Input({ value, onChange, type = 'text', placeholder }: { value: string; onChange: (v: string) => void; type?: string; placeholder?: string }) {
    return <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} style={inputStyle} />;
}

export function Check({ label, checked, onChange, disabled }: { label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
    return (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.84rem', color: disabled ? ui.muted : ui.ink }}>
            <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
            {label}
        </label>
    );
}

export function FormGrid({ children }: { children: React.ReactNode }) {
    return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 12 }}>{children}</div>;
}

export function Section({ title, children, actions }: { title: string; children: React.ReactNode; actions?: React.ReactNode }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 800, color: ui.ink }}>{title}</div>
                {actions ? <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{actions}</div> : null}
            </div>
            {children}
        </div>
    );
}

export function Note({ children }: { children: React.ReactNode }) {
    return <div style={{ fontSize: '0.8rem', color: ui.ink2, background: '#fafbfd', border: `1px solid ${ui.line2}`, borderRadius: ui.radiusSm, padding: '10px 12px' }}>{children}</div>;
}
